import { Box, Button, Chip, MenuItem, Paper, Select, Stack, Typography } from '@mui/material';

import type {
    AIManagerConversation,
    AIManagerFunnelStageKey,
    AIManagerKanbanColumn,
} from 'src/services/AIManagersService';

const CARD_BORDER = 'var(--lm-border)';
const CARD_RADIUS = '8px';

type FunnelStage = {
    key: AIManagerFunnelStageKey;
    title: string;
    tone: string;
};

const FUNNEL_STAGES: FunnelStage[] = [
    { key: 'new_contact', title: 'Новый контакт', tone: 'var(--lm-muted)' },
    { key: 'interest_present', title: 'Есть интерес', tone: '#2E6BCB' },
    { key: 'target_lead', title: 'Целевой лид', tone: 'var(--lm-success-ink)' },
    { key: 'decision_making', title: 'Решение', tone: '#B7791F' },
    { key: 'payment', title: 'Оплата', tone: '#7C3AED' },
    { key: 'closed', title: 'Закрыто', tone: 'var(--lm-success-ink)' },
    { key: 'rejected', title: 'Отказ', tone: 'var(--lm-disabled)' },
];

function formatDate(value?: string) {
    if (!value) {
        return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

function getConversationTitle(conversation: AIManagerConversation) {
    return conversation.sourceAuthor || conversation.leadInfo?.author || conversation.sourceChatTitle || 'Диалог';
}

function getConversationPreview(conversation: AIManagerConversation) {
    return conversation.latestMessagePreview || conversation.sourceMessage || 'Сообщений пока нет';
}

export function normalizeColumnsAfterMove(
    columns: AIManagerKanbanColumn[],
    updated: AIManagerConversation
): AIManagerKanbanColumn[] {
    const previousStage = columns.find((column) => column.items.some((item) => item.id === updated.id))?.key;

    return columns.map((column) => {
        const withoutMoved = column.items.filter((item) => item.id !== updated.id);
        const nextItems = column.key === updated.salesStage ? [updated, ...withoutMoved] : withoutMoved;
        const movedFromColumn = previousStage && previousStage !== updated.salesStage && column.key === previousStage;
        const movedToColumn =
            previousStage && previousStage !== updated.salesStage && column.key === updated.salesStage;
        const nextCount = movedToColumn ? column.count + 1 : movedFromColumn ? column.count - 1 : column.count;

        return {
            ...column,
            items: nextItems,
            count: previousStage ? Math.max(0, nextCount) : nextItems.length,
        };
    });
}

function ConversationCard({
    conversation,
    stages,
    isMoving,
    onMove,
    expanded = false,
    onOpen,
}: {
    expanded?: boolean;
    onOpen?: (id: number) => void;
    conversation: AIManagerConversation;
    stages: FunnelStage[];
    isMoving: boolean;
    onMove: (conversation: AIManagerConversation, stage: AIManagerFunnelStageKey) => void;
}) {
    const source = conversation.sourceChatTitle || conversation.leadInfo?.chatName || 'Telegram';
    const updatedAt = formatDate(conversation.updatedAt || conversation.createdAt);

    return (
        <Paper
            variant="outlined"
            sx={{
                bgcolor: 'var(--lm-surface)',
                borderColor: CARD_BORDER,
                borderRadius: CARD_RADIUS,
                boxShadow: 'none',
                p: 1.5,
            }}
        >
            <Stack spacing={1.25}>
                <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={1}
                >
                    <Box minWidth={0}>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 800 }}
                            noWrap
                        >
                            {getConversationTitle(conversation)}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="secondary.main"
                            noWrap
                        >
                            {source}
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        label={conversation.messageCount}
                        title={`Сообщений: ${conversation.messageCount}`}
                        sx={{
                            bgcolor: 'var(--lm-canvas)',
                            border: '1px solid var(--lm-border)',
                            color: 'var(--lm-muted)',
                            fontWeight: 700,
                            height: 24,
                        }}
                    />
                </Box>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'var(--lm-text)',
                        display: '-webkit-box',
                        WebkitLineClamp: expanded ? undefined : 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {getConversationPreview(conversation)}
                </Typography>
                {updatedAt && (
                    <Typography
                        variant="caption"
                        color="secondary.main"
                    >
                        {updatedAt}
                    </Typography>
                )}
                {onOpen && (
                    <Button
                        variant="text"
                        size="small"
                        sx={{ alignSelf: 'flex-start', px: 0 }}
                        aria-label={`Открыть диалог: ${getConversationTitle(conversation)}`}
                        id={`conversation-trigger-${conversation.id}`}
                        onClick={() => onOpen(conversation.id)}
                    >
                        Открыть диалог
                    </Button>
                )}
                <Select
                    size="small"
                    value={conversation.salesStage}
                    inputProps={{ 'aria-label': `Стадия: ${getConversationTitle(conversation)}` }}
                    disabled={isMoving}
                    onChange={(event) => onMove(conversation, event.target.value as AIManagerFunnelStageKey)}
                    sx={{
                        borderRadius: CARD_RADIUS,
                        fontSize: 13,
                        '& .MuiSelect-select': {
                            py: 0.75,
                        },
                    }}
                >
                    {stages.map((stage) => (
                        <MenuItem
                            key={stage.key}
                            value={stage.key}
                        >
                            {stage.title}
                        </MenuItem>
                    ))}
                </Select>
            </Stack>
        </Paper>
    );
}

export function SalesFunnelBoard({
    columns,
    movingConversationId,
    onMove,
    onOpen,
}: {
    columns: AIManagerKanbanColumn[];
    movingConversationId: number | null;
    onMove: (conversation: AIManagerConversation, stage: AIManagerFunnelStageKey) => void;
    onOpen?: (id: number) => void;
}) {
    const columnsByKey = new Map(columns.map((column) => [column.key, column]));

    return (
        <Box
            role="region"
            aria-label="Стадии воронки"
            tabIndex={0}
            sx={{
                overflowX: 'auto',
                pb: 1,
                width: 1,
                '&::-webkit-scrollbar': {
                    height: 8,
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'var(--lm-border)',
                    borderRadius: '999px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'var(--lm-muted)',
                    borderRadius: '999px',
                },
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gap: 1.5,
                    gridTemplateColumns: {
                        xs: `repeat(${FUNNEL_STAGES.length}, minmax(260px, 84vw))`,
                        md: `repeat(${FUNNEL_STAGES.length}, minmax(190px, 1fr))`,
                    },
                    minWidth: {
                        md: 1340,
                    },
                }}
            >
                {FUNNEL_STAGES.map((stage) => {
                    const column = columnsByKey.get(stage.key);
                    const items = column?.items || [];
                    const count = column?.count || 0;

                    return (
                        <Box
                            key={stage.key}
                            sx={{
                                backgroundColor: 'var(--lm-surface)',
                                border: `1px solid ${CARD_BORDER}`,
                                borderRadius: CARD_RADIUS,
                                boxShadow: 'none',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: {
                                    xs: 420,
                                    md: 560,
                                },
                                p: 1.5,
                            }}
                        >
                            <Box
                                alignItems="flex-start"
                                display="flex"
                                gap={1}
                                justifyContent="space-between"
                            >
                                <Typography
                                    color="var(--lm-text)"
                                    variant="h3"
                                    sx={{ fontSize: 16, lineHeight: 1.25 }}
                                >
                                    {column?.title || stage.title}
                                </Typography>
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        backgroundColor: 'var(--lm-canvas)',
                                        border: '1px solid var(--lm-border)',
                                        borderRadius: '999px',
                                        color: 'var(--lm-muted)',
                                        display: 'flex',
                                        flexShrink: 0,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        height: 24,
                                        justifyContent: 'center',
                                        minWidth: 28,
                                        px: 1,
                                    }}
                                >
                                    {count}
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    backgroundColor: stage.tone,
                                    borderRadius: '999px',
                                    height: 3,
                                    mt: 1.5,
                                    opacity: 0.9,
                                }}
                            />
                            <Stack
                                spacing={1}
                                mt={1.5}
                            >
                                {items.map((conversation) => (
                                    <ConversationCard
                                        key={conversation.id}
                                        conversation={conversation}
                                        stages={FUNNEL_STAGES}
                                        isMoving={movingConversationId === conversation.id}
                                        onMove={onMove}
                                        onOpen={onOpen}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

export function ReviewQueueList({
    conversations,
    movingConversationId,
    onMove,
    disabled = false,
    onOpen,
}: {
    conversations: AIManagerConversation[];
    movingConversationId: number | null;
    onMove: (conversation: AIManagerConversation, stage: AIManagerFunnelStageKey) => void;
    disabled?: boolean;
    onOpen?: (id: number) => void;
}) {
    return (
        <Box
            display="grid"
            gap={1.5}
            sx={{ gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}
        >
            {conversations.map((conversation) => (
                <ConversationCard
                    key={conversation.id}
                    conversation={conversation}
                    stages={FUNNEL_STAGES}
                    expanded
                    isMoving={disabled || movingConversationId === conversation.id}
                    onMove={onMove}
                    onOpen={onOpen}
                />
            ))}
        </Box>
    );
}
